import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Link,
  Alert,
  InputAdornment,
  Divider,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { xpConfigurationsService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const XpConfigurationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isNew = location.pathname === '/xp-configurations/new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    actionType: '',
    displayName: '',
    description: '',
    baseXp: 0,
    calculationType: 'fixed',
    percentageValue: null,
    minXp: null,
    maxXp: null,
    isActive: true,
    dailyLimit: null,
    monthlyLimit: null,
  });

  // Types d'actions prédéfinis
  const actionTypes = [
    { value: 'registration', label: 'Inscription' },
    { value: 'purchase', label: 'Achat' },
    { value: 'referral', label: 'Parrainage' },
    { value: 'review', label: 'Avis client' },
    { value: 'first_purchase', label: 'Premier achat' },
    { value: 'social_share', label: 'Partage social' },
    { value: 'newsletter', label: 'Inscription newsletter' },
    { value: 'profile_completion', label: 'Complétion du profil' },
    { value: 'birthday', label: 'Anniversaire' },
    { value: 'daily_login', label: 'Connexion quotidienne' },
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!isNew && id) {
        const configData = await xpConfigurationsService.getById(id);
        if (configData && configData.data) {
          setFormData({
            actionType: configData.data.actionType || '',
            displayName: configData.data.displayName || '',
            description: configData.data.description || '',
            baseXp: configData.data.baseXp || 0,
            calculationType: configData.data.calculationType || 'fixed',
            percentageValue: configData.data.percentageValue,
            minXp: configData.data.minXp,
            maxXp: configData.data.maxXp,
            isActive: configData.data.isActive !== undefined ? configData.data.isActive : true,
            dailyLimit: configData.data.dailyLimit,
            monthlyLimit: configData.data.monthlyLimit,
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // Validation
      if (formData.calculationType === 'percentage' && !formData.percentageValue) {
        setError('Le pourcentage est requis pour le type de calcul "Pourcentage"');
        return;
      }

      const submitData = {
        ...formData,
        baseXp: parseInt(formData.baseXp) || 0,
        percentageValue: formData.percentageValue ? parseFloat(formData.percentageValue) : null,
        minXp: formData.minXp ? parseInt(formData.minXp) : null,
        maxXp: formData.maxXp ? parseInt(formData.maxXp) : null,
        dailyLimit: formData.dailyLimit ? parseInt(formData.dailyLimit) : null,
        monthlyLimit: formData.monthlyLimit ? parseInt(formData.monthlyLimit) : null,
      };

      if (isNew) {
        await xpConfigurationsService.create(submitData);
      } else {
        await xpConfigurationsService.update(id, submitData);
      }

      navigate('/xp-configurations');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError(error.response?.data?.error || 'Erreur lors de la sauvegarde de la configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return <LoadingSpinner message="Chargement de la configuration..." />;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/xp-configurations"
          onClick={(e) => {
            e.preventDefault();
            navigate('/xp-configurations');
          }}
        >
          Configurations XP
        </Link>
        <Typography color="text.primary">
          {isNew ? 'Nouvelle configuration' : 'Modifier la configuration'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isNew ? 'Nouvelle configuration XP' : 'Modifier la configuration XP'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Informations de base */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Informations de base
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Type d'action</InputLabel>
                <Select
                  value={formData.actionType}
                  onChange={(e) => handleChange('actionType', e.target.value)}
                  label="Type d'action"
                >
                  {actionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom d'affichage"
                value={formData.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                required
                helperText="Nom affiché dans l'interface"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                helperText="Description optionnelle de cette règle XP"
              />
            </Grid>

            {/* Configuration de l'XP */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Configuration de l'XP
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type de calcul</InputLabel>
                <Select
                  value={formData.calculationType}
                  onChange={(e) => handleChange('calculationType', e.target.value)}
                  label="Type de calcul"
                >
                  <MenuItem value="fixed">XP Fixe</MenuItem>
                  <MenuItem value="percentage">Pourcentage du montant</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.calculationType === 'fixed' ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="XP de base"
                  type="number"
                  value={formData.baseXp}
                  onChange={(e) => handleChange('baseXp', e.target.value)}
                  required
                  InputProps={{
                    endAdornment: <InputAdornment position="end">XP</InputAdornment>,
                  }}
                  helperText="XP fixe gagné pour cette action"
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pourcentage"
                    type="number"
                    value={formData.percentageValue || ''}
                    onChange={(e) => handleChange('percentageValue', e.target.value)}
                    required
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{ step: 0.01 }}
                    helperText="% du montant converti en XP"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="XP Minimum"
                    type="number"
                    value={formData.minXp || ''}
                    onChange={(e) => handleChange('minXp', e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">XP</InputAdornment>,
                    }}
                    helperText="XP minimum garanti"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="XP Maximum"
                    type="number"
                    value={formData.maxXp || ''}
                    onChange={(e) => handleChange('maxXp', e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">XP</InputAdornment>,
                    }}
                    helperText="XP maximum plafonné"
                  />
                </Grid>
              </>
            )}

            {/* Limites et restrictions */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Limites et restrictions
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limite quotidienne"
                type="number"
                value={formData.dailyLimit || ''}
                onChange={(e) => handleChange('dailyLimit', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">fois/jour</InputAdornment>,
                }}
                helperText="Nombre max de fois par jour (vide = illimité)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limite mensuelle"
                type="number"
                value={formData.monthlyLimit || ''}
                onChange={(e) => handleChange('monthlyLimit', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">fois/mois</InputAdornment>,
                }}
                helperText="Nombre max de fois par mois (vide = illimité)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="Actif"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/xp-configurations')}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                >
                  {saving ? 'Sauvegarde...' : (isNew ? 'Créer' : 'Modifier')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Aide et exemples */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          💡 Exemples de configuration
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body2" gutterBottom>
            <strong>Achat (Pourcentage):</strong> 1% du montant = XP (ex: 100€ → 100 XP)
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            <strong>Inscription (Fixe):</strong> 100 XP une seule fois
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            <strong>Parrainage (Fixe):</strong> 50 XP par filleul validé
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            <strong>Avis (Fixe avec limite):</strong> 10 XP par avis, max 3/jour
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default XpConfigurationEdit;















